import { Letter } from "src/js/stores/letter/store";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";

import { sanitiseVueObservable } from "../pipeline/helpers/sanitiseVueObservable";
import { convertFormDataToHtml } from "./convertFormDataToHtml";
import { Recipient } from "../typings/Recipient";
import { SiteLetterInput } from "../typings/Letter";
import { AvailableCountryCodes } from "../typings/CountryCode";
import { Geometry } from "../typings/Geometry";
import { PaperFormat } from "../typings/PaperFormat";

export type TemplateInput = {
  letter: Letter;
  letterName: string;
  pageCount: number;
  siteBoundaryImage?: string;
};

const replaceLineBreaks = (body: string): string => {
  return body.replace(/(?:\r\n|\r|\n)/g, "{{br}}");
};

export function formatLetterRequest(
  { letter, letterName, pageCount, siteBoundaryImage }: TemplateInput,
  recipient: Recipient,
  options?: {
    hideRecipientAddress?: boolean;
  }
): SiteLetterInput {
  const usAccess = hasFeature(Feature.usAccess);
  const { propertyAddress, title, ...recipientProps } = recipient;

  const htmlTemplate = convertFormDataToHtml(letter, {
    pageCount,
    propertyAddress,
    hideRecipientAddress: options?.hideRecipientAddress,
    recipientProvince: recipientProps.region,
  });

  const {
    logo,
    bodyText,
    bodyTextPage2,
    signature,
    signaturePage,
    showSiteBoundary,
    vatRegistrationNumber,
    companyRegistrationNumber,
    senderAddressLine1,
    senderCity,
    senderName,
    senderPostcode,
    senderProvince,
  } = letter;

  const htmlVariables = {
    ...(propertyAddress && { property_address: propertyAddress }),
    sent_date: new Date().toLocaleDateString(),
    greeting: letter.greeting,
    body_text_page_1: replaceLineBreaks(bodyText),
    ...(!usAccess && { title: title }),
  };

  const footerVariables = {
    company_registration_number: companyRegistrationNumber,
    vat_registration_number: vatRegistrationNumber,
  };
  Object.assign(htmlVariables, footerVariables);

  if (signaturePage) {
    Object.assign(htmlVariables, {
      sign_off: letter.signOff,
      contact_name: letter.contactName,
      contact_email: letter.contactEmail,
      contact_phone: letter.contactPhone,
    });
  }

  if (pageCount > 1) {
    Object.assign(htmlVariables, {
      body_text_page_2: bodyTextPage2 ? replaceLineBreaks(bodyTextPage2) : undefined,
      ...(showSiteBoundary && { site_boundary_image: siteBoundaryImage }),
    });
  }

  const recipientCountryCode =
    recipientProps.countryCode ??
    (usAccess ? AvailableCountryCodes.UNITED_STATES : AvailableCountryCodes.UNITED_KINGDOM);

  const senderCountryCode = usAccess
    ? AvailableCountryCodes.UNITED_STATES
    : AvailableCountryCodes.UNITED_KINGDOM;

  const { type, coordinates } = sanitiseVueObservable(recipientProps.geometry);

  const siteLetterInput = {
    name: letterName,
    title_geometry: { type, coordinates } as Geometry,
    html: htmlTemplate,
    sender_address_line_1: senderAddressLine1,
    sender_city: senderCity,
    sender_name: senderName,
    sender_post_code: senderPostcode,
    sender_province: senderProvince,
    sender_country_code: senderCountryCode,
    recipient_address_line_1: recipientProps.addressLine1,
    recipient_name: recipientProps.fullName,
    recipient_post_code: recipientProps.postCode,
    recipient_city: recipientProps.city,
    recipient_province: recipientProps.region,
    recipient_country_code: recipientCountryCode,
    ...(logo?.s3ObjectKey && { logo_s3_object_key: logo?.s3ObjectKey }),
    ...(signaturePage &&
      signature?.s3ObjectKey && { signature_s3_object_key: signature?.s3ObjectKey }),
    variables: htmlVariables,
    paper_format: usAccess ? PaperFormat.US_LETTER : PaperFormat.A4,
  };

  return siteLetterInput;
}
